import { CheckIcon, ChevronDownIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface FilterOption {
  value: string;
  label: string;
}

export const FilterDropdown = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
  showSelectedInline = false,
  showAllOption = false,
}: {
  label: string;
  options: FilterOption[];
  selected: string | null;
  onToggle: (value: string) => void;
  /** Consumed by the page-level chips, not the dropdown itself. */
  onClear?: () => void;
  /** When true, shows "Label: Selected" in the trigger instead of just "Label". */
  showSelectedInline?: boolean;
  /** When true, prepends an "All" item that clears the filter. */
  showAllOption?: boolean;
}) => {
  const selectedOption = options.find((option) => option.value === selected);

  const triggerLabel =
    showSelectedInline && selectedOption
      ? `${label}: ${selectedOption.label}`
      : showSelectedInline && showAllOption
        ? `${label}: All`
        : label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outlineSecondary"
          size="sm"
          className={cn(
            "h-9 gap-1.5 rounded-full",
            selectedOption && "border-slate-300 bg-slate-50 text-slate-900",
          )}
        >
          {triggerLabel}
          <ChevronDownIcon className="h-4 w-4 text-slate-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[320px] min-w-[200px] overflow-auto"
      >
        {showAllOption && onClear && (
          <DropdownMenuItem
            className={cn("justify-between gap-2", selected === null && "bg-slate-50 font-medium")}
            onSelect={onClear}
          >
            All
            <CheckIcon
              className={cn(
                "h-4 w-4 shrink-0",
                selected === null ? "opacity-100" : "opacity-0",
              )}
            />
          </DropdownMenuItem>
        )}
        {options.length === 0 ? (
          <DropdownMenuItem disabled>No options</DropdownMenuItem>
        ) : (
          options.map((option) => {
            const isSelected = option.value === selected;

            return (
              <DropdownMenuItem
                key={option.value}
                className={cn("justify-between gap-2", isSelected && "bg-slate-50 font-medium")}
                onSelect={() => {
                  onToggle(option.value);
                }}
              >
                {option.label}
                <CheckIcon
                  className={cn(
                    "h-4 w-4 shrink-0",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                />
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
